import { useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import toast from "react-hot-toast";

import {
  EmptyState,
  PageHeader,
  StatusBadge,
  TenantGateBanner,
  useTenantGate,
} from "../../components/ui";
import { useTenantScope } from "../../hooks/useTenantScope";
import { useDebounce } from "../../hooks/useDebounce";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { UserAvatar } from "../Attendances/components/PersonCell";
import { useGetAttendanceEventOptionsQuery } from "../../store/services/attendances";
import {
  useCompleteBoEventAttendanceMutation,
  useGetBoEventAttendanceRosterQuery,
  useMarkAllBoEventAttendanceMutation,
  useMarkBoEventAttendanceMutation,
  useUpdateBoEventAttendanceMutation,
} from "../../store/services/eventAttendanceBo";
import {
  AttendanceSubject,
  EventAttendee,
  canCompleteAttendance,
} from "../../models/eventAttendance";
import {
  getApiErrorCode,
  getApiErrorMessage,
  getApiErrorPayload,
  isObjectRecord,
} from "../../utils/functions/api-errors";
import { formatDateWithoutSeconds } from "../../utils/functions/date";

const SEARCH_DEBOUNCE_MS = 300;

const SECTION_FLAG = "attendance_bo_section_enabled";
const TENANT_ACTION = "tomar asistencia";
const OUTSIDE_WINDOW_CODE = "OUTSIDE_WINDOW";

/**
 * Mensaje amable cuando el evento elegido está fuera de la ventana de asistencia (409
 * OUTSIDE_WINDOW). La API devuelve los bordes en `error.details[0]`; con eso distinguimos si la
 * toma todavía no arrancó o si ya cerró, en vez del texto genérico. Devuelve null si el error no
 * es de ventana (lo maneja el camino de error común).
 */
const outsideWindowMessage = (error: unknown): string | null => {
  if (getApiErrorCode(error) !== OUTSIDE_WINDOW_CODE) return null;

  const payload = getApiErrorPayload(error);
  const envelope = isObjectRecord(payload) ? payload.error : undefined;
  const details = isObjectRecord(envelope) ? envelope.details : undefined;
  const first = Array.isArray(details) ? details[0] : undefined;
  const start = isObjectRecord(first) && typeof first.window_start === "string" ? first.window_start : undefined;
  const end = isObjectRecord(first) && typeof first.window_end === "string" ? first.window_end : undefined;

  const now = Date.now();
  if (start && now < new Date(start).getTime()) {
    return `La toma de asistencia todavía no comenzó. Se habilita el ${formatDateWithoutSeconds(start)}.`;
  }
  if (end && now > new Date(end).getTime()) {
    return `La toma de asistencia ya cerró. El período terminó el ${formatDateWithoutSeconds(end)}.`;
  }
  return "La toma de asistencia está fuera del período habilitado.";
};

/**
 * Al abrir el dropdown enfoca el buscador para tipear nombre/ID directo, sin un click extra en
 * el campo. Sólo hay un panel abierto a la vez, así que el `.p-dropdown-filter` del DOM es el de
 * este dropdown.
 */
const focusDropdownFilter = (): void => {
  requestAnimationFrame(() => {
    document.querySelector<HTMLInputElement>(".p-dropdown-filter")?.focus();
  });
};

type PresenceValue = "present" | "absent";

const PRESENCE_OPTIONS: Array<{ label: string; value: PresenceValue }> = [
  { label: "Presente", value: "present" },
  { label: "Ausente", value: "absent" },
];

const subjectOf = (attendee: EventAttendee): AttendanceSubject | null => {
  if (attendee.userId !== null) return { userId: attendee.userId };
  if (attendee.companionId !== null) return { companionId: attendee.companionId };
  return null;
};

export const EventAttendance = () => {
  const scope = useTenantScope();
  const gate = useTenantGate();
  const selectedTenantId = gate.tenantId;

  const { enabled: isSectionFlagEnabled, loading: isLoadingSectionFlag } =
    useIsFeatureFlagEnabled(SECTION_FLAG);

  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [eventSearch, setEventSearch] = useState("");
  const debouncedEventSearch = useDebounce(eventSearch, SEARCH_DEBOUNCE_MS);

  // Al cambiar de tenant seleccionado, se descarta el evento elegido: era de la institución
  // anterior y el backend rechazaría la toma (403 BO_FORBIDDEN).
  const [tenantOfSelection, setTenantOfSelection] = useState(selectedTenantId);
  if (tenantOfSelection !== selectedTenantId) {
    setTenantOfSelection(selectedTenantId);
    setSelectedEventId(null);
    setEventSearch("");
  }

  const { currentData: eventOptionsData } = useGetAttendanceEventOptionsQuery(
    { search: debouncedEventSearch.trim(), tenantId: selectedTenantId, purpose: "registration" },
    { skip: !gate.allowed }
  );
  const eventOptions = (eventOptionsData ?? []).map((event) => ({
    label: `${event.name} (#${event.id})`,
    value: event.id,
  }));

  const {
    currentData: roster,
    isFetching: isLoadingRoster,
    error: rosterError,
  } = useGetBoEventAttendanceRosterQuery(selectedEventId as number, {
    skip: selectedEventId === null || !gate.allowed,
  });

  const [markAttendance, markState] = useMarkBoEventAttendanceMutation();
  const [updateAttendance, updateState] = useUpdateBoEventAttendanceMutation();
  const [markAll, markAllState] = useMarkAllBoEventAttendanceMutation();
  const [complete, completeState] = useCompleteBoEventAttendanceMutation();

  const isMutating =
    markState.isLoading ||
    updateState.isLoading ||
    markAllState.isLoading ||
    completeState.isLoading;

  const header = <PageHeader title="Tomar asistencia" />;

  if (scope.loading || isLoadingSectionFlag) {
    return (
      <div className="p-card p-4">
        {header}
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (!gate.allowed) {
    return (
      <div className="p-card p-4">
        {header}
        <TenantGateBanner action={TENANT_ACTION} gate={gate} />
      </div>
    );
  }

  if (!isSectionFlagEnabled) {
    return (
      <div className="p-card p-4">
        {header}
        <div className="flex justify-content-center p-4">
          <Message severity="info" text="La sección está deshabilitada para este tenant." />
        </div>
      </div>
    );
  }

  const setPresence = async (attendee: EventAttendee, value: PresenceValue): Promise<void> => {
    const isPresent = value === "present";
    const subject = subjectOf(attendee);
    if (subject === null || selectedEventId === null) return;
    try {
      if (attendee.id === null) {
        await markAttendance({ eventId: selectedEventId, subject, isPresent }).unwrap();
      } else {
        await updateAttendance({
          eventId: selectedEventId,
          attendanceId: attendee.id,
          isPresent,
        }).unwrap();
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "No se pudo guardar la marca");
    }
  };

  const onMarkAll = async (): Promise<void> => {
    if (selectedEventId === null) return;
    try {
      await markAll(selectedEventId).unwrap();
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "No se pudo marcar a todos");
    }
  };

  const onComplete = async (): Promise<void> => {
    if (selectedEventId === null) return;
    try {
      await complete(selectedEventId).unwrap();
      toast.success("Asistencia tomada");
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? "No se pudo cerrar la toma de asistencia");
    }
  };

  const summary = roster?.summary;
  const isCompleted = roster?.session.status === "completed";
  const canComplete = summary !== undefined && canCompleteAttendance(summary);

  const presenceTemplate = (attendee: EventAttendee) => {
    // El anfitrión que asiste viene presente forzado desde el padrón y el backend rechaza
    // marcarlo: se muestra como estado fijo, sin el control de marcado.
    if (attendee.isHost) {
      return <StatusBadge label="Presente" tone="success" />;
    }
    return (
      <div className="attendance-presence-cell">
        <SelectButton
          value={attendee.isPresent === null ? null : attendee.isPresent ? "present" : "absent"}
          options={PRESENCE_OPTIONS}
          onChange={(e) => {
            if (e.value === null || e.value === undefined) return; // no permitir "desmarcar"
            void setPresence(attendee, e.value as PresenceValue);
          }}
          disabled={isMutating}
          allowEmpty={false}
        />
      </div>
    );
  };

  const nameTemplate = (attendee: EventAttendee) => (
    <div className="flex align-items-center gap-2">
      <UserAvatar name={attendee.name} avatarUrl={attendee.avatarUrl} />
      <div className="flex flex-column">
        <span>{attendee.name}</span>
        {attendee.isHost && <small className="text-primary font-medium">Anfitrión</small>}
        {attendee.isCompanion && <small className="text-500">Acompañante</small>}
      </div>
    </div>
  );

  return (
    <div className="p-card p-4">
      {header}

      <div className="flex flex-column gap-2 mb-4" style={{ maxWidth: 420 }}>
        <label htmlFor="eventAttendanceEvent" className="text-600">
          Evento
        </label>
        <Dropdown
          inputId="eventAttendanceEvent"
          value={selectedEventId}
          options={eventOptions}
          optionLabel="label"
          optionValue="value"
          placeholder="Elegí un evento"
          filter
          showClear
          onFilter={(e) => setEventSearch(e.filter)}
          onChange={(e) => setSelectedEventId(e.value ?? null)}
          onShow={focusDropdownFilter}
          filterPlaceholder="Nombre o ID del evento"
          emptyFilterMessage="No se encontraron eventos"
          emptyMessage="No hay eventos para tomar asistencia"
        />
      </div>

      {selectedEventId === null && (
        <EmptyState entity="eventos" icon="pi pi-calendar" />
      )}

      {selectedEventId !== null && rosterError !== undefined && (
        <Message
          severity={outsideWindowMessage(rosterError) !== null ? "warn" : "error"}
          className="w-full justify-content-start"
          text={
            outsideWindowMessage(rosterError) ??
            getApiErrorMessage(rosterError) ??
            "No se pudo abrir la toma de asistencia"
          }
        />
      )}

      {selectedEventId !== null && isLoadingRoster && roster === undefined && (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      )}

      {selectedEventId !== null && roster !== undefined && (
        <>
          <div className="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div className="flex align-items-center gap-2">
              <StatusBadge
                label={isCompleted ? "Cerrada" : "En progreso"}
                tone={isCompleted ? "success" : "pending"}
              />
              {summary !== undefined && (
                <span className="text-600">
                  {summary.present} presentes · {summary.absent} ausentes · {summary.unmarked} sin
                  marcar · {summary.total} en total
                </span>
              )}
            </div>
            <Button
              label="Marcar todos presentes"
              icon="pi pi-check"
              outlined
              size="small"
              onClick={() => void onMarkAll()}
              loading={markAllState.isLoading}
              disabled={isMutating || (summary?.total ?? 0) === 0}
            />
          </div>

          <DataTable
            value={roster.attendees}
            dataKey="name"
            emptyMessage={<EmptyState entity="inscriptos" />}
            responsiveLayout="scroll"
          >
            <Column header="Persona" body={nameTemplate} />
            <Column header="Asistencia" body={presenceTemplate} style={{ width: "15rem" }} />
          </DataTable>

          <div className="flex justify-content-end mt-4">
            <Button
              label={isCompleted ? "Guardar cambios" : "Tomar asistencia"}
              icon="pi pi-check-circle"
              onClick={() => void onComplete()}
              loading={completeState.isLoading}
              disabled={isMutating || !canComplete}
              tooltip={
                canComplete ? undefined : "Marcá a todos los inscriptos antes de cerrar la toma"
              }
              tooltipOptions={{ position: "top", showOnDisabled: true }}
            />
          </div>
        </>
      )}
    </div>
  );
};
